import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useApi } from '../../app/providers';
import type { Board } from '../../lib/api';
import { Button } from '../../ui';
import { Toolbar } from '../../ui/Toolbar';
import { useSession } from '../auth/session';
import { MoveBoardDialog } from './BoardActions';
import { settle } from './bulk';
import { bulkActions, inverseOf, type BulkActionId, type Inverse } from './model/actions';
import { useFavorites, useOrg, useProjects } from './queries';
import { useUndoable } from './undo';

const LABEL: Record<BulkActionId, string> = {
  move: 'dashboard.moveTo',
  duplicate: 'dashboard.duplicate',
  favorite: 'dashboard.favorite',
  archive: 'dashboard.archive',
  trash: 'dashboard.toTrash',
};

/** Thrown by `run` when nothing could be done (every operation failed). */
export class BulkFailed extends Error {}

/** What a selection may do and how to do it (the bar and the keyboard share it); `move` opens the dialog instead. */
export function useBulk() {
  const { t } = useTranslation();
  const api = useApi();
  const session = useSession();
  const { org } = useOrg();
  const projects = useProjects(org?.id);
  const favorites = useFavorites();
  const undoable = useUndoable();
  const userId = session.status === 'signed-in' ? session.user.id : '';
  const archivedProjects = new Set((projects.data ?? []).filter((p) => p.archivedAt).map((p) => p.id));
  const favoriteIds = new Set((favorites.data ?? []).map((b) => b.id));

  // A selection spans projects: an archived project refuses writes to its own boards only.
  const allowedFor = (boards: readonly Board[]) =>
    bulkActions(boards, { org, userId, canCopy: api.capabilities.boardCopy, projectArchived: false }).map((a) => ({
      ...a,
      allowedIds: a.allowedIds.filter((id) => a.id === 'favorite' || !archivedProjects.has(boards.find((b) => b.id === id)!.projectId)),
    }));

  const run = async (id: Exclude<BulkActionId, 'move'>, boards: readonly Board[], allowedIds: readonly string[]) => {
    const byId = new Map(boards.map((b) => [b.id, b]));
    const ids = id === 'favorite' ? allowedIds.filter((b) => !favoriteIds.has(b)) : allowedIds;
    const op = (boardId: string): Promise<unknown> => {
      const b = byId.get(boardId)!;
      if (id === 'duplicate') return api.duplicateBoard(boardId, { projectId: b.projectId, title: t('dashboard.copyOf', { title: b.title }) });
      if (id === 'favorite') return api.setFavorite(boardId, true);
      return api.lifecycle('board', id, boardId);
    };
    const { done } = await settle(ids, op);
    if (ids.length > 0 && done.length === 0) throw new BulkFailed();
    const inverses: Inverse[] = id === 'duplicate' ? [] : done.map((b) => inverseOf(id, byId.get(b)!, { favorite: true })!);
    const message = done.length < ids.length ? t(`dashboard.bulk.partial.${id}`, { done: done.length, total: ids.length }) : t(`dashboard.bulk.done.${id}`, { count: done.length });
    await undoable(async () => {}, inverses, message);
  };

  return { org, allowedFor, run };
}

/**
 * The bar of a multiple selection (P16-4): each action names how many of the selected boards it may act on when that is
 * not all of them, acts on those only, reports partial failures and offers one Undo for what succeeded.
 */
export function BulkBar({ boards, onClear }: { boards: readonly Board[]; onClear: () => void }) {
  const { t } = useTranslation();
  const { org, allowedFor, run } = useBulk();
  const [moving, setMoving] = useState<Board[] | null>(null);
  const [error, setError] = useState(false);
  const actions = allowedFor(boards);

  const act = async (id: BulkActionId, allowedIds: string[]) => {
    setError(false);
    if (id === 'move') return setMoving(boards.filter((b) => allowedIds.includes(b.id)));
    try {
      await run(id, boards, allowedIds);
      onClear();
    } catch {
      setError(true);
    }
  };

  return (
    <>
      <Toolbar.Root
        label={t('dashboard.bulk.label')}
        className="os-pop fixed bottom-6 left-1/2 z-30 flex max-w-[calc(100vw-2rem)] -translate-x-1/2 flex-wrap items-center gap-2 rounded-xl bg-surface p-2 shadow-lg ring-1 ring-border"
      >
        <span className="px-2 font-sans text-sm font-medium text-fg">{t('dashboard.bulk.selected', { count: boards.length })}</span>
        {actions.map(({ id, allowedIds }) => {
          const label = t(LABEL[id]);
          return (
            // An action that has nothing to act on is skipped by the arrow keys (it cannot take the focus anyway).
            <Toolbar.Button
              key={id}
              focusableWhenDisabled={false}
              disabled={allowedIds.length === 0}
              onClick={() => void act(id, allowedIds)}
              render={<Button size="sm" />}
            >
              {allowedIds.length < boards.length ? t('dashboard.bulk.subset', { label, allowed: allowedIds.length, total: boards.length }) : label}
            </Toolbar.Button>
          );
        })}
        <Toolbar.Button onClick={onClear} render={<Button size="sm" variant="ghost" />}>
          {t('dashboard.bulk.clear')}
        </Toolbar.Button>
        {error && (
          <p role="alert" className="w-full px-2 font-sans text-xs text-danger">
            {t('dashboard.bulk.failed')}
          </p>
        )}
      </Toolbar.Root>
      {moving && org && (
        <MoveBoardDialog
          boards={moving}
          org={org}
          onClose={() => setMoving(null)}
        />
      )}
    </>
  );
}
